import React, { useState } from "react";

import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

import { RejectSheet } from "./RejectSheet";

interface PricingApprovalActionsProps {
  selectedOrder: SalesRequest;
  clientName: string;
  onOrderStatusChange?: () => void;
  onCloseOrderDialog?: () => void;
}

export const PricingApprovalActions: React.FC<PricingApprovalActionsProps> = ({
  selectedOrder,
  clientName,
  onOrderStatusChange,
  onCloseOrderDialog,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejectSheetOpen, setIsRejectSheetOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const profileStore = useProfileStore();

  // Check if user has permission to approve pricing
  const canApprovePricing =
    profileStore.role === "superadmin" ||
    profileStore.role === "admin" ||
    profileStore.role === "facturacion";

  // Only show for pending_pricing status and authorized users
  if (selectedOrder.status !== "pending_pricing" || !canApprovePricing) {
    return null;
  }

  const handleApprovePricing = async () => {
    setIsSubmitting(true);
    try {
      // Get the sales condition to determine next step
      const { data: salesConditionData, error: salesConditionError } =
        await supabase
          .from("sales_conditions")
          .select("name, requires_prepayment, type")
          .eq("id", selectedOrder.salesConditionId || "")
          .single();

      let newStatus: "pending" | "prepaid" = "pending";
      let nextStepName = "Autorización Cobranza";

      // Determine next step based on sales condition
      let isTransferenciaAnticipada = false;
      if (!salesConditionError && salesConditionData) {
        isTransferenciaAnticipada =
          salesConditionData.name === "TRANSFERENCIA ANTICIPADA" ||
          salesConditionData.name
            ?.toLowerCase()
            .includes("transferencia anticipada") ||
          Boolean(salesConditionData.requires_prepayment) ||
          salesConditionData.type === "prepaid";
      }

      if (isTransferenciaAnticipada) {
        newStatus = "prepaid";
        nextStepName = "Anticipado Pendiente de Pago";
      }

      // Update the sales request status
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Status history is recorded by the record_sales_request_status_change
      // trigger on the UPDATE above (no manual insert -> no duplicate row).

      toast({
        title: "Precio autorizado",
        description: `La nota de venta ha sido movida a ${nextStepName}`,
      });

      // Update local state to reflect the changes
      if (onOrderStatusChange) {
        onOrderStatusChange();
      }
      // Return to the board so the card animates into its new column.
      onCloseOrderDialog?.();
    } catch (error: any) {
      console.error("Error al autorizar precio:", error.message);
      toast({
        title: "Error",
        description: "No se pudo autorizar el precio",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectPricing = async () => {
    if (!rejectionReason) {
      toast({
        title: "Error",
        description: "Debe seleccionar un motivo de rechazo",
        variant: "destructive",
      });
      return;
    }

    // Use custom reason if "Otro motivo" is selected and custom reason is provided
    const finalRejectionReason =
      rejectionReason === "Otro motivo" && customReason.trim()
        ? customReason.trim()
        : rejectionReason;

    setIsSubmitting(true);
    try {
      // Update the sales request status to rejected
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "rejected",
          rejected_reason: finalRejectionReason,
          approved_rejected_by: profileStore.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Status history is recorded by the record_sales_request_status_change
      // trigger on the UPDATE above (no manual insert -> no duplicate row).

      toast({
        title: "Precio rechazado",
        description: "La nota de venta ha sido rechazada",
      });

      setIsRejectSheetOpen(false);
      setRejectionReason("");
      setCustomReason("");
      // Update local state to reflect the changes
      if (onOrderStatusChange) {
        onOrderStatusChange();
      }
    } catch (error: any) {
      console.error("Error al rechazar precio:", error.message);
      toast({
        title: "Error",
        description: "No se pudo rechazar el precio",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50/50 p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-purple-800">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
          Autorización de Precio
        </h3>
        <p className="mb-4 text-sm text-purple-700">
          Esta nota de venta requiere autorización de precio antes de continuar
          con el proceso.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            onClick={() => setIsRejectSheetOpen(true)}
            variant="outline"
            className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
            size="sm"
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Rechazar Precio
          </Button>
          <Button
            onClick={handleApprovePricing}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
            size="sm"
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {isSubmitting ? "Autorizando..." : "Autorizar Precio"}
          </Button>
        </div>
      </div>

      <RejectSheet
        isOpen={isRejectSheetOpen}
        setIsOpen={setIsRejectSheetOpen}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        customReason={customReason}
        setCustomReason={setCustomReason}
        isSubmitting={isSubmitting}
        onReject={handleRejectPricing}
      />
    </>
  );
};
